import React from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowUp, ArrowDown, Plus, Wallet } from '@phosphor-icons/react';

export default function MRKhataPage() {
  const [summary, setSummary] = React.useState([]);
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [sumRes, entRes] = await Promise.all([api.getKhataSummary(), api.getKhata()]);
      setSummary(sumRes.data);
      setEntries(entRes.data);
    } catch {}
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const totalReceivable = summary.reduce((s, r) => s + r.receivable, 0);
  const totalPayable = summary.reduce((s, r) => s + r.payable, 0);
  const net = totalReceivable - totalPayable;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="px-4 pt-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
              Khata Book
            </h2>
            <p className="text-[10px] font-medium" style={{ color: '#8A94A6' }}>Payables & Receivables</p>
          </div>
          <Button onClick={() => setShowForm(true)}
            className="h-9 px-3 text-xs font-bold rounded-lg hidden"
            style={{ background: '#0A2540', color: 'white' }}>
            <Plus size={14} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="px-4">
        <div className="p-4 rounded-xl" style={{ background: '#0A2540' }}>
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={18} weight="duotone" color="white" />
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>Net Balance</span>
          </div>
          <p className="text-2xl font-extrabold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>
            ₹{Math.abs(net).toLocaleString()}
            <span className="text-xs ml-2 font-medium" style={{ color: net >= 0 ? '#34C759' : '#FF3B30' }}>
              {net >= 0 ? 'Receivable' : 'Payable'}
            </span>
          </p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[9px] font-bold uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>To Receive</p>
              <p className="text-lg font-bold text-white mt-0.5" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{totalReceivable.toLocaleString()}</p>
            </div>
            <div className="p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[9px] font-bold uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>To Pay</p>
              <p className="text-lg font-bold text-white mt-0.5" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{totalPayable.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Ledger */}
      <div className="px-4">
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: '#8A94A6' }}>Customer Ledger</p>
        <div className="rounded-xl border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          {loading ? (
            <div className="p-8 text-center" style={{ color: '#8A94A6' }}>Loading...</div>
          ) : summary.length === 0 ? (
            <div className="p-8 text-center" style={{ color: '#8A94A6' }}>No entries yet</div>
          ) : summary.map((s, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3 active:bg-slate-50 transition-colors cursor-pointer"
              onClick={() => navigate(`/mr/party-ledger/${s.customer_id}`)}
              style={{ borderBottom: i < summary.length - 1 ? '1px solid #E2E4E9' : 'none' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ background: s.net >= 0 ? '#34C759' : '#FF3B30' }}>
                  {s.customer_name?.charAt(0)}
                </div>
                <span className="text-sm font-semibold" style={{ color: '#0A2540' }}>{s.customer_name}</span>
              </div>
              <span className="text-sm font-bold" style={{
                color: s.net >= 0 ? '#34C759' : '#FF3B30',
                fontVariantNumeric: 'tabular-nums'
              }}>
                {s.net >= 0 ? '+' : '-'}₹{Math.abs(s.net).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="px-4 space-y-2 pb-4">
        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Recent Transactions</p>
        {entries.slice(0, 10).map((e, i) => (
          <div key={i} className="flex items-center gap-2.5 p-3 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: e.entry_type === 'receivable' ? '#34C75910' : '#FF3B3010' }}>
              {e.entry_type === 'receivable' ?
                <ArrowDown size={16} weight="bold" style={{ color: '#34C759' }} /> :
                <ArrowUp size={16} weight="bold" style={{ color: '#FF3B30' }} />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold truncate" style={{ color: '#0A2540' }}>{e.customer_name}</p>
              <p className="text-[10px]" style={{ color: '#8A94A6' }}>{e.description} | {e.date}</p>
            </div>
            <span className="text-xs font-bold shrink-0" style={{
              color: e.entry_type === 'receivable' ? '#34C759' : '#FF3B30',
              fontVariantNumeric: 'tabular-nums'
            }}>
              {e.entry_type === 'receivable' ? '+' : '-'}₹{e.amount.toLocaleString()}
            </span>
          </div>
        ))}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-sm p-5">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>New Khata Entry</DialogTitle></DialogHeader>
          <KhataForm onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KhataForm({ onSave }) {
  const [customers, setCustomers] = React.useState([]);
  const [form, setForm] = React.useState({ customer_id: '', customer_name: '', entry_type: 'receivable', amount: 0, description: '' });

  React.useEffect(() => { api.getCustomers().then(r => setCustomers(r.data)); }, []);

  const save = async () => {
    try { await api.createKhataEntry(form); onSave(); } catch {}
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Customer</Label>
        <Select value={form.customer_id} onValueChange={v => {
          const c = customers.find(x => x.id === v);
          setForm(f => ({ ...f, customer_id: v, customer_name: c?.name || '' }));
        }}>
          <SelectTrigger className="h-10 mt-1 rounded-lg"><SelectValue placeholder="Select Customer" /></SelectTrigger>
          <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Type</Label>
        <Select value={form.entry_type} onValueChange={v => setForm(f => ({ ...f, entry_type: v }))}>
          <SelectTrigger className="h-10 mt-1 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="receivable">Receivable</SelectItem>
            <SelectItem value="payable">Payable</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Amount</Label>
        <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))} className="h-10 mt-1 rounded-lg" />
      </div>
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Description</Label>
        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-10 mt-1 rounded-lg" />
      </div>
      <Button onClick={save} className="w-full h-12 text-sm font-bold rounded-xl" style={{ background: '#0A2540', color: 'white' }}>Save Entry</Button>
    </div>
  );
}
